import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@easyflow/ui-frontend/components/ui/popover';
import { ChevronDown, BellOff, Coffee, PhoneCall, Circle, Moon } from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { messengerApi } from '../lib/messengerApi';
import type { PresenceStatus } from '../types';
import { cn } from '@/lib/utils';

interface Props {
  current: PresenceStatus;
  onChange: (next: PresenceStatus) => void;
}

const OPTIONS: Array<{ value: PresenceStatus; label: string; color: string; icon: React.ComponentType<{ className?: string }> }> = [
  { value: 'online', label: 'Online', color: 'text-emerald-500', icon: Circle },
  { value: 'away', label: 'Assente', color: 'text-amber-500', icon: Coffee },
  { value: 'in-call', label: 'In chiamata', color: 'text-red-500', icon: PhoneCall },
  { value: 'dnd', label: 'Non disturbare', color: 'text-violet-500', icon: BellOff },
  { value: 'offline', label: 'Invisibile', color: 'text-gray-400', icon: Moon },
];

const DND_PRESETS: Array<{ label: string; minutes: number | null }> = [
  { label: '30 minuti', minutes: 30 },
  { label: '1 ora', minutes: 60 },
  { label: '4 ore', minutes: 240 },
  { label: 'Fino a domani', minutes: 60 * 24 },
  { label: 'Disattiva', minutes: null },
];

// Task #1660 — menu presence v2. Imposta lo stato self e (per DND) il
// timer di scadenza con bypass mention opzionale.

export function PresenceMenu({ current, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [allowMentions, setAllowMentions] = useState(true);
  const [saving, setSaving] = useState(false);

  const setStatus = async (next: PresenceStatus) => {
    setSaving(true);
    try {
      await messengerApi.setPresenceStatus(next);
      onChange(next);
    } finally { setSaving(false); }
  };

  const setDnd = async (minutes: number | null) => {
    setSaving(true);
    try {
      const until = minutes ? new Date(Date.now() + minutes * 60_000).toISOString() : null;
      await messengerApi.setDnd(until, allowMentions);
      if (minutes) {
        await messengerApi.setPresenceStatus('dnd');
        onChange('dnd');
      } else {
        await messengerApi.setPresenceStatus('online');
        onChange('online');
      }
      setOpen(false);
    } finally { setSaving(false); }
  };

  const currentOpt = (OPTIONS.find((o) => o.value === current) || OPTIONS[0])!;
  const Icon = currentOpt.icon;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1.5 px-2 py-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          title="Cambia stato presence"
        >
          <Icon className={cn('h-3 w-3', currentOpt.color, currentOpt.value === 'online' && 'fill-current')} />
          <span className="text-[11px] font-medium">{currentOpt.label}</span>
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <div className="space-y-0.5">
          {OPTIONS.map((opt) => {
            const I = opt.icon;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setStatus(opt.value)}
                disabled={saving}
                className={cn(
                  'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors',
                  current === opt.value ? 'bg-blue-50 dark:bg-blue-950/30 font-medium' : 'hover:bg-gray-50 dark:hover:bg-gray-800',
                )}
              >
                <I className={cn('h-3.5 w-3.5', opt.color, opt.value === 'online' && 'fill-current')} />
                <span>{opt.label}</span>
              </button>
            );
          })}
        </div>
        <div className="mt-3 pt-3 border-t space-y-2">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">Non disturbare per…</div>
          {DND_PRESETS.map((p) => (
            <Button
              key={p.label}
              size="sm"
              variant="ghost"
              className="w-full justify-start rounded-md h-7 text-xs hover:scale-100 active:scale-100"
              onClick={() => setDnd(p.minutes)}
              disabled={saving}
            >
              {p.label}
            </Button>
          ))}
          <Label className="flex items-center gap-2 cursor-pointer pt-1">
            <input
              type="checkbox"
              checked={allowMentions}
              onChange={(e) => setAllowMentions(e.target.checked)}
              className="rounded border-gray-300"
            />
            <span className="text-[11px] text-muted-foreground">Ricevi comunque le menzioni</span>
          </Label>
        </div>
      </PopoverContent>
    </Popover>
  );
}
